import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Menu,
  MessageSquare,
  Bell,
  User,
  LogOut,
  ChevronDown,
  ArrowLeft,
  Settings,
  Briefcase,
  TrendingUp,
  LineChart,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/stores/uiStore'
import { cn, gravatarUrl } from '@/lib/utils'

/**
 * Header component with navigation and user menu.
 *
 * This is a simplified version of the v1 header, focusing on essential
 * navigation while maintaining the same visual identity.
 */
export function Header() {
  const { username, isAuthenticated, logout } = useAuth()
  const { toggleMobileMenu, mobileMenuOpen } = useUIStore()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Use current origin to stay on the same domain (devui vs dev vs stable)
  const v1BaseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  return (
    <header className="sticky top-0 z-50 bg-nav-dark text-white shadow-md">
      <div className="flex h-14 items-center px-4">
        {/* Mobile menu button */}
        <button
          className="mr-3 rounded-md p-2 hover:bg-white/10 lg:hidden"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Logo / Brand */}
        <Link to="/" className="flex items-center gap-2 text-white hover:text-white">
          <img
            src="/v2/logo.svg"
            alt="Alpha-Trader"
            className="h-8"
            onError={(e) => {
              // Fallback if logo not found
              e.currentTarget.style.display = 'none'
            }}
          />
          <span className="hidden text-lg font-semibold sm:inline">
            Alpha-Trader
            <span className="ml-1 rounded bg-primary px-1.5 py-0.5 text-xs font-medium">
              v2 Beta
            </span>
          </span>
        </Link>

        {/* Back to v1 link */}
        <a
          href={v1BaseUrl}
          className="ml-4 flex items-center gap-1 text-sm text-gray-300 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back to v1</span>
        </a>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Navigation items */}
        {isAuthenticated && (
          <nav className="flex items-center gap-2">
            {/* Chat link */}
            <Link
              to="/chat"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <MessageSquare className="h-5 w-5" />
              <span className="hidden sm:inline">Chat</span>
            </Link>

            {/* Portfolio link */}
            <Link
              to="/portfolio"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <Briefcase className="h-5 w-5" />
              <span className="hidden sm:inline">Portfolio</span>
            </Link>

            {/* Securities link */}
            <Link
              to="/securities"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <TrendingUp className="h-5 w-5" />
              <span className="hidden sm:inline">Securities</span>
            </Link>

            {/* Trading link */}
            <Link
              to="/trading"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <LineChart className="h-5 w-5" />
              <span className="hidden sm:inline">Trading</span>
            </Link>

            {/* Profile link */}
            <Link
              to="/profile"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <User className="h-5 w-5" />
              <span className="hidden sm:inline">Profile</span>
            </Link>

            {/* Notifications (link to v1) */}
            <a
              href={`${v1BaseUrl}/events`}
              className={cn(
                'relative flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                'hover:bg-white/10'
              )}
            >
              <Bell className="h-5 w-5" />
              <span className="hidden sm:inline">Notifications</span>
            </a>

            {/* User menu with dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                  'hover:bg-white/10',
                  userMenuOpen && 'bg-white/10'
                )}
              >
                <img
                  src={gravatarUrl('', 32)}
                  alt={username || 'User'}
                  className="h-6 w-6 rounded-full"
                />
                <span className="hidden sm:inline">{username}</span>
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', userMenuOpen && 'rotate-180')}
                />
              </button>

              {/* Dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </Link>
                  <Link
                    to="/portfolio"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Briefcase className="h-4 w-4" />
                    Portfolio
                  </Link>
                  <a
                    href={`${v1BaseUrl}/settings`}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </a>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      logout()
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>
        )}

        {/* Login link for unauthenticated users */}
        {!isAuthenticated && (
          <a
            href={v1BaseUrl}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            <User className="h-5 w-5" />
            Login
          </a>
        )}
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-white/10 lg:hidden">
          <nav className="flex flex-col p-4">
            <Link
              to="/chat"
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
              onClick={toggleMobileMenu}
            >
              <MessageSquare className="h-5 w-5" />
              Chat
            </Link>
            <Link
              to="/portfolio"
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
              onClick={toggleMobileMenu}
            >
              <Briefcase className="h-5 w-5" />
              Portfolio
            </Link>
            <Link
              to="/securities"
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
              onClick={toggleMobileMenu}
            >
              <TrendingUp className="h-5 w-5" />
              Securities
            </Link>
            <Link
              to="/trading"
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
              onClick={toggleMobileMenu}
            >
              <LineChart className="h-5 w-5" />
              Trading
            </Link>
            <Link
              to="/profile"
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
              onClick={toggleMobileMenu}
            >
              <User className="h-5 w-5" />
              Profile
            </Link>
            <a
              href={`${v1BaseUrl}/events`}
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
            >
              <Bell className="h-5 w-5" />
              Notifications
            </a>
            <a
              href={`${v1BaseUrl}/settings`}
              className="flex items-center gap-3 rounded-md px-3 py-3 hover:bg-white/10"
            >
              <Settings className="h-5 w-5" />
              Settings
            </a>
            <button
              onClick={logout}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-left hover:bg-white/10"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </nav>
        </div>
      )}
    </header>
  )
}
